import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Upload, Download, Filter, Wand2, RefreshCw } from "lucide-react";
import { PageHeader, PageBody, Field } from "@/components/PageChrome";
import { Input, Button } from "@/components/controls";
import { T } from "@/lib/testIds";
import PaginationBar from "@/components/PaginationBar";
import useClientPagination from "@/hooks/useClientPagination";
import FieldFilterBar, { applyFieldFilters } from "@/components/FieldFilterBar";
import FilterPresets from "@/components/FilterPresets";
import ImageThumb from "@/components/ImageThumb";

const FILTER_FIELDS = [
  { key: "asin", label: "ASIN", type: "text" },
  { key: "image_url", label: "Image URL", type: "text" },
  { key: "marketplace", label: "Marketplace", type: "select", opts: () => ["amazon", "flipkart", "myntra", "shopify"] },
  { key: "source", label: "Source", type: "select", opts: () => ["manual", "import", "scrape"] },
  { key: "notes", label: "Notes", type: "text" },
];

export default function AsinImages() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [advFilters, setAdvFilters] = useState({});
  const [scrapeAsin, setScrapeAsin] = useState("");
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [needsLookup, setNeedsLookup] = useState(0);

  const load = async (searchQuery) => {
    setBusy(true);
    try {
      const qp = searchQuery !== undefined ? searchQuery : q;
      const { data } = await api.get("/asin-images", { params: qp ? { q: qp } : {} });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const refreshStatus = async () => {
    try {
      const { data } = await api.get("/asin-images/backfill-status");
      setBackfillStatus(data);
      return data;
    } catch { return null; }
  };
  // Initial status load + rolling refresh whenever backfill is in flight.
  useEffect(() => { refreshStatus(); }, []);
  useEffect(() => {
    if (!isBackfilling) return;
    const id = setInterval(async () => {
      const s = await refreshStatus();
      // Auto-refresh grid so newly-scraped ASINs appear live.
      load();
      if (s && s.inflight_scrapes === 0) {
        setIsBackfilling(false);
        toast.success(`Backfill complete — ${s.cached_asins} ASINs cached, ${s.missing_images} orders still missing images.`);
      }
    }, 4000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [isBackfilling]);

  const filtered = useMemo(
    () => rows.filter((r) => applyFieldFilters(r, advFilters, FILTER_FIELDS)),
    [rows, advFilters]
  );
  const activeAdvCount = Object.keys(advFilters).filter((k) => {
    const v = advFilters[k];
    if (!v) return false;
    if (typeof v === "object") return Object.values(v).some((x) => x !== "" && x !== undefined);
    return true;
  }).length;
  const pag = useClientPagination(filtered, "asin_images");

  const save = async () => {
    try {
      if (creating) await api.post("/asin-images", creating);
      else await api.patch(`/asin-images/${editing.id}`, editing);
      toast.success("Saved");
      setCreating(null); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this ASIN mapping?")) return;
    try { await api.delete(`/asin-images/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const runScrape = async () => {
    const asin = (scrapeAsin || "").trim();
    if (!asin) { toast.error("Enter an ASIN or Amazon URL"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/asin-images/scrape/${encodeURIComponent(asin)}`);
      if (data.image_url) toast.success(`Scraped: ${data.image_url}`);
      else toast.error("Scrape returned no image — try again later");
      setScrapeAsin(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const backfillOrders = async () => {
    if (!window.confirm("Walk every order with a blank image_url and fill from the ASIN cache? This does not start any new scraping.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/asin-images/backfill-orders");
      setNeedsLookup(data.needs_lookup || 0);
      toast.success(`${data.quick_updates} filled instantly · ${data.needs_lookup} order(s) still need a lookup`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const startLookups = async () => {
    if (!window.confirm(`Start scraping Amazon for ${needsLookup} ASIN(s) still missing an image? This runs in the background.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/asin-images/backfill-orders/start-lookups");
      toast.success(`${data.scheduled_scrapes} scrape task(s) queued`);
      setNeedsLookup(0);
      if (data.scheduled_scrapes > 0) {
        setIsBackfilling(true);
        await refreshStatus();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const importCsv = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/asin-images/import/csv", form, { headers: { "Content-Type": "multipart/form-data" } });
      const skip = (data.skipped || []).length;
      toast.success(`Imported: ${data.created} new, ${data.updated} updated${skip ? ` · ${skip} skipped` : ""}`);
      if (skip) console.warn("[asin import] skipped rows:", data.skipped);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const draft = editing || creating || {};
  const set = (k, v) => (creating ? setCreating({ ...creating, [k]: v }) : setEditing({ ...editing, [k]: v }));

  return (
    <>
      <PageHeader title="ASIN Images" subtitle="Central cache of Amazon product images — orders auto-fill from here"
                  testId={T.moduleTitle("asin_images")} module="asin_images">
        <div className="relative">
          <input
            data-testid="asin-images-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(e.currentTarget.value); }}
            placeholder="Search ASIN, URL, notes…"
            className="h-8 px-2 w-56 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <input
          data-testid="asin-images-scrape-input"
          value={scrapeAsin}
          onChange={(e) => setScrapeAsin(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && runScrape()}
          placeholder="Scrape one ASIN…"
          className="h-8 px-2 w-40 text-[13px] font-mono border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <Button onClick={runScrape} testId="asin-images-scrape-btn" disabled={busy || !scrapeAsin.trim()}>
          <Wand2 size={13} /> Scrape
        </Button>
        <Button onClick={backfillOrders} testId="asin-images-backfill-btn" disabled={busy}>
          <RefreshCw size={13} /> Backfill orders
        </Button>
        {needsLookup > 0 && (
          <Button onClick={startLookups} testId="asin-images-start-lookups-btn" disabled={busy}>
            <Wand2 size={13} /> Start image lookups ({needsLookup})
          </Button>
        )}
        <Button onClick={() => downloadFile("/asin-images/templates/csv", "asin_images_template.csv")} testId="asin-images-template-btn">
          <Download size={13} /> Template
        </Button>
        <label className="h-8 px-3 text-[12px] font-medium rounded-sm inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid="asin-images-import-btn">
          <Upload size={13} /> Import
          <input type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) importCsv(f).finally(() => { e.target.value = ""; }); }} />
        </label>
        <Button onClick={() => downloadFile("/asin-images/export/csv", "asin_images.csv")} testId="asin-images-export-btn">
          <Download size={13} /> Export
        </Button>
        <Button onClick={() => setShowFilterBar((s) => !s)} testId="filter-asin_images-btn">
          <Filter size={13} /> Filters
          {activeAdvCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9.5px] font-bold text-white bg-blue-600 rounded-full">
              {activeAdvCount}
            </span>
          )}
        </Button>
        <FilterPresets moduleKey="asin_images" value={advFilters} onLoad={setAdvFilters} />
        <Button variant="primary" onClick={() => setCreating({ marketplace: "amazon", source: "manual" })} testId={T.newBtn("asin_images")}>
          <Plus size={13} /> New Entry
        </Button>
      </PageHeader>

      <PageBody>
        {backfillStatus && (
          <div className="mb-3 flex items-center gap-3 text-[11.5px] font-mono">
            <div className="inline-flex items-center gap-1.5 h-7 px-2.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm">
              <span className="font-semibold">Cached ASINs:</span> <span data-testid="asin-images-status-cached">{backfillStatus.cached_asins}</span>
            </div>
            <div className={`inline-flex items-center gap-1.5 h-7 px-2.5 border rounded-sm ${backfillStatus.missing_images ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
              <span className="font-semibold">Orders missing image:</span> <span data-testid="asin-images-status-missing">{backfillStatus.missing_images}</span>
            </div>
            <div className={`inline-flex items-center gap-1.5 h-7 px-2.5 border rounded-sm ${backfillStatus.inflight_scrapes ? "bg-indigo-50 text-indigo-800 border-indigo-200 animate-pulse" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
              <span className="font-semibold">In-flight scrapes:</span> <span data-testid="asin-images-status-inflight">{backfillStatus.inflight_scrapes}</span>
            </div>
            {isBackfilling && (
              <div className="inline-flex items-center gap-1.5 h-7 px-2.5 bg-blue-50 text-blue-800 border border-blue-200 rounded-sm">
                <RefreshCw size={11} className="animate-spin" /> Backfill running…
              </div>
            )}
            <button onClick={refreshStatus} className="text-slate-500 hover:text-slate-800 h-7 px-1.5 border border-transparent hover:border-slate-200 rounded-sm" title="Refresh counts" data-testid="asin-images-status-refresh">
              <RefreshCw size={12} />
            </button>
          </div>
        )}

        {showFilterBar && (
          <div className="mb-3">
            <FieldFilterBar moduleKey="asin_images" fields={FILTER_FIELDS} value={advFilters} onChange={setAdvFilters} />
          </div>
        )}

        <div className="border border-slate-200 bg-white rounded-sm">
          <table className="erp-table" data-testid="asin-images-grid">
            <thead>
              <tr>
                <th>Image</th>
                <th>ASIN</th>
                <th>Image URL</th>
                <th>Marketplace</th>
                <th>Source</th>
                <th>Notes</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pag.pageRows.map((r) => (
                <tr key={r.id} data-testid={`asin-images-row-${r.asin}`} onClick={() => setEditing({ ...r })} className="cursor-pointer">
                  <td>
                    {r.image_url
                      ? <ImageThumb src={r.image_url} alt={r.asin} size={64} testId={`asin-thumb-${r.asin}`} />
                      : <div className="w-16 h-16 border border-dashed border-slate-300 rounded-sm text-[9px] text-slate-400 flex items-center justify-center">no img</div>}
                  </td>
                  <td className="font-mono">{r.asin}</td>
                  <td className="font-mono text-[11px] truncate max-w-[300px]" title={r.image_url}>{r.image_url || "—"}</td>
                  <td>{r.marketplace || "—"}</td>
                  <td>
                    <span className={`inline-block h-5 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${
                      r.source === "scrape" ? "bg-indigo-50 text-indigo-800 border-indigo-200"
                      : r.source === "import" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                      : "bg-slate-50 text-slate-700 border-slate-200"}`}>
                      {r.source || "manual"}
                    </span>
                  </td>
                  <td className="text-slate-600 text-[11.5px]">{r.notes || "—"}</td>
                  <td className="font-mono text-[10.5px] text-slate-500">{r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => del(r.id)} data-testid={`asin-images-del-${r.asin}`}
                            className="text-slate-500 hover:text-red-700"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-500">
                  {busy ? "Loading…" : rows.length ? "No entries match the current filters." : "No ASIN images yet. Add one manually or run Scrape."}
                </td></tr>
              )}
            </tbody>
          </table>
          <PaginationBar {...pag} />
        </div>
      </PageBody>

      {(editing || creating) && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => { setEditing(null); setCreating(null); }}>
          <div className="w-full max-w-md bg-white shadow-xl h-full overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-slate-200">
              <h3 className="font-semibold">{creating ? "New ASIN Image" : `Edit ${editing?.asin}`}</h3>
              <button onClick={() => { setEditing(null); setCreating(null); }}><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              {draft.image_url && (
                <div className="flex justify-center">
                  <img referrerPolicy="no-referrer" src={draft.image_url} alt="" className="max-h-40 border border-slate-200 rounded-sm" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
              )}
              <Field label="ASIN"><Input testId="asin-images-form-asin" value={draft.asin || ""} onChange={(e) => set("asin", e.target.value)} placeholder="B08N5WRWNW" /></Field>
              <Field label="Image URL"><Input testId="asin-images-form-url" value={draft.image_url || ""} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…" /></Field>
              <Field label="Marketplace">
                <select value={draft.marketplace || "amazon"} onChange={(e) => set("marketplace", e.target.value)}
                        className="h-8 px-2 w-full text-[13px] border border-slate-200 rounded-sm">
                  <option value="amazon">amazon</option>
                  <option value="flipkart">flipkart</option>
                  <option value="myntra">myntra</option>
                  <option value="shopify">shopify</option>
                </select>
              </Field>
              <Field label="Notes"><Input value={draft.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <Button onClick={() => { setEditing(null); setCreating(null); }}>Cancel</Button>
              <Button variant="primary" onClick={save} testId="asin-images-save-btn">Save</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
